import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { copyObject } from 'webapp/utils/common';

import CommonAskToConnectButtonComponent from 'webapp/components/common/ask-to-connect-button';
import type { ActionOption } from 'webapp/components/form/select-with-action';
import FormSelectWithClickableLabel3Component from 'webapp/components/form/select-with-clickable-label3';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { SourceData } from 'webapp/models-custom/resolve-sources-data';
import { StatusObjectAction } from 'webapp/models-custom/status-object';

import type { onConnectSourceResult } from 'webapp/services/connect';
import type { SourceListEntry } from 'webapp/services/model-manager/sources';

// a source list entry with the select's clickable label3
type ChoiceSourceEntry = SourceListEntry & {
	disabled?: boolean;
	onLabel3Click?: () => void;
};

interface Args {
	sourceData: SourceData;
	onChoose: (sourceData: SourceData, sourceId: string) => void;
	checkPublicTemplatesSpace?: boolean;
	isDatasetSupported?: boolean;
	showAvatar?: boolean;
	skipLabel?: boolean;
	isDisabled?: boolean;
	isUseTemplate?: boolean;
}

export default class ResolveDataItemChoice extends Component<Args> {
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('connect') declare connectService: Service.Connect;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;

	get selectName(): string {
		return `itemchoice${this.args.sourceData.sourceType?.key}`;
	}

	get selectedSourceId(): string | null {
		return this.args.sourceData.connectedSourceId ?? null;
	}

	get placeholder() {
		return `Select a ${this.args.sourceData.sourceType?.name} data source`;
	}

	get datasourceList() {
		const sourceType = this.args.sourceData.sourceType,
			sourceTypeId = sourceType?.id;
		const typeSources = sourceTypeId
			? this.mmSourcesService.sourceListSpecific.filter(
					(item) => item.type === sourceTypeId && !item.isDataset,
				)
			: [];
		const sources: ChoiceSourceEntry[] = copyObject(typeSources);

		// disable sources that have label3 (are in error status)
		const isPublictemplatesSpace =
			this.args.checkPublicTemplatesSpace && this.accountService.isPublictemplatesSpace;

		sources.forEach((source) => {
			if (!isPublictemplatesSpace) {
				source.disabled = !source.isAbstract && !!source.isDisabled;

				if (!source.isAbstract) {
					source.label3 = source.statusActionElementHtml;
					source.onLabel3Click = () => {
						if (source.isDataset) {
							this.navigationService.transitionToRoute('datamanager.connected');
						} else {
							void this.reconnectSource(
								sourceType?.key,
								+source.value, // the source id
								source.canReauthorize,
							);
						}
					};
				} else {
					if (source.statusObjectAction === StatusObjectAction.SOURCE_CONNECTED) {
						source.label3 = undefined;
						source.onLabel3Click = undefined;
					}
				}
			}

			source.group = '';

			if (!this.args.showAvatar) {
				source.avatar = '';
			}
		});

		return sources;
	}

	get sourceActions(): ActionOption[] {
		const sourceType = this.args.sourceData.sourceType;

		return [
			{
				id: '',
				text: `Connect another ${sourceType.name}`,
				html: `<a class="source-add">Connect another ${sourceType.name}</a>`,
				isSpecial: true,
				position: 'bottom',
				onClick: () => {
					void this.connectSource(sourceType.key);
				},
			},
		];
	}

	@action
	chooseSource(value: string | null) {
		this.args.onChoose(this.args.sourceData, value!);
	}

	connectSource(sourceTypeKey: string, sourceId?: number, canReauthorize?: boolean) {
		return this.connectService
			.connectSourceFromIdOrKey(sourceTypeKey, {
				sourceId: sourceId,
				reauthorize: canReauthorize,
				origin: 'ResolvedataItemChoice',
				isUseTemplate: this.args.isUseTemplate ?? false,
			})
			.then((result: onConnectSourceResult) => {
				if (result.isSuccess) {
					// after the source metrics are added to store
					next(() => {
						this.chooseSource(String(result.source.id));
					});
				}
			});
	}

	reconnectSource(sourceTypeKey: string, sourceId: number, canReauthorize?: boolean) {
		return this.connectSource(sourceTypeKey, sourceId, canReauthorize);
	}
	get hasAvailableSources(): boolean {
		return this.datasourceList.length > 0;
	}

	@action
	connectAndChoose(): void {
		void this.connectSource(this.args.sourceData.sourceType.key);
	}

	<template>
		{{#if this.hasAvailableSources}}
			<div class="choice">
				{{#unless @skipLabel}}
					<p>
						You already have
						{{@sourceData.sourceType.name}}
						connected. Select which one to use:
					</p>
				{{/unless}}
				<FormSelectWithClickableLabel3Component
					name={{this.selectName}}
					@selected={{this.selectedSourceId}}
					@options={{this.datasourceList}}
					@actionOptions={{this.sourceActions}}
					@onChange={{this.chooseSource}}
					@placeholder={{this.placeholder}}
					@isNumeric={{false}}
					@showFloatlabel={{false}}
					@isDisabled={{@isDisabled}}
				/>
			</div>
		{{else}}
			<div class="actions">
				<CommonAskToConnectButtonComponent @model={{@sourceData.sourceType}} />
				<SubmitButtonComponent
					@text="Connect"
					@icon="Connect"
					@noDelay={{true}}
					@onTrigger={{this.connectAndChoose}}
					@theme="green"
				/>
			</div>
		{{/if}}
	</template>
}
